/**
 * Live subject-registry broadcasts — subject created/updated/merged, alias
 * asserted/revoked, edge opened/closed — delivered through the shared
 * `global_events` room (PRD-0219 D-7).
 *
 * This domain exists because that room has exactly one entry point: a
 * client-emitted `subscribe_global`. `websocketManager.subscribe()` only
 * attaches a local handler — it never sends the join and never replays it
 * after a reconnect — so a surface built on that alone would compile, run,
 * and silently receive nothing.
 */

import type {
  SmemActivationPolicyChangedBroadcast,
  SmemAliasEventPayload,
  SmemAskSpendGuardPayload,
  SmemClaimDispatchPayload,
  SmemClaimExecutionPolicyBroadcast,
  SmemCompositionPolicyChangedBroadcast,
  SmemConsolidationPolicyChangedBroadcast,
  SmemConsolidationProposalBroadcast,
  SmemConsolidationRunCompletedBroadcast,
  SmemEdgeEventPayload,
  SmemEpisodeArchivalBroadcast,
  SmemEpisodeEventPayload,
  SmemErasureCompletedBroadcast,
  SmemSubjectEventPayload,
} from '@/types/smem'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

/**
 * The seven registry broadcasts, in the order the backend emits them
 * (subject lifecycle, then aliases, then edges). Exported so the consuming
 * hook and its test share one source of truth for the list.
 */
export const SMEM_EVENTS = [
  'smem_subject_created',
  'smem_subject_updated',
  'smem_subject_merged',
  'smem_subject_alias_created',
  'smem_subject_alias_revoked',
  'smem_subject_edge_created',
  'smem_subject_edge_closed',
] as const

export type SmemEventName = (typeof SMEM_EVENTS)[number]

/** The three payload families the seven events carry. */
export type SmemEventPayload =
  | SmemSubjectEventPayload
  | SmemAliasEventPayload
  | SmemEdgeEventPayload

/** What one dispatch carries: which event fired, and its payload. */
type SmemDispatch = [SmemEventName, SmemEventPayload]

/**
 * The episode-composition + composition-policy broadcasts (PRD-0236 R-11) —
 * a second, additive tuple sharing this module rather than extending
 * SMEM_EVENTS: the subjects hook's invalidator must not see episode bursts,
 * and vice versa (F-20). Episode events reach the global room only because of
 * the D-18 emitter amendment (`_EpisodeEmitter.broadcast` routes
 * `project_id=None`); the composition-policy broadcast was global-room from
 * the start (identity + version only, D-3).
 *
 * The two archival literals (PRD-0246 R-9) join this tuple rather than the
 * new consolidation registry below: they are stamped on the episode itself —
 * the same entity composed/declined/rejected already describe — and their
 * consumer is whatever reads an episode's own state, not the consolidation
 * panel's run/proposal/policy hooks (F-15's grouping is by consumer, not by
 * originating PRD).
 */
export const SMEM_EPISODE_EVENTS = [
  'smem_episode_composed',
  'smem_episode_declined',
  'smem_episode_rejected',
  'smem_composition_policy_changed',
  'smem_episode_archived',
  'smem_episode_unarchived',
] as const

export type SmemEpisodeEventName = (typeof SMEM_EPISODE_EVENTS)[number]

/** The three payload families the six events carry. */
export type SmemEpisodeEventDispatchPayload =
  | SmemEpisodeEventPayload
  | SmemCompositionPolicyChangedBroadcast
  | SmemEpisodeArchivalBroadcast

type SmemEpisodeDispatch = [SmemEpisodeEventName, SmemEpisodeEventDispatchPayload]

/**
 * The claim lifecycle broadcasts (PRD-0239 D-7) — a third, additive tuple
 * sharing this module rather than extending either registry above: the
 * claims invalidator must not see subject or episode bursts, and vice versa
 * (F-20's isolation rule applied to a third surface). Identity-only, riding
 * the global room like every broadcast in this file; personal claims are
 * suppressed entirely by the emitter (0238), never filtered client-side.
 *
 * The recommendation and run frames (PRD-0381 D-12) share it because one
 * consumer, useClaimsRealtime, owns every claim-review cache entry they make
 * stale. Each literal is a key of SmemClaimDispatchPayload, which names the
 * payload it carries.
 */
export const SMEM_CLAIM_EVENTS = [
  'smem_claim_created',
  'smem_claim_status_changed',
  'smem_claim_recommendation_changed',
  'smem_claim_review_run_changed',
] as const satisfies readonly (keyof SmemClaimDispatchPayload)[]

export type SmemClaimEventName = (typeof SMEM_CLAIM_EVENTS)[number]

/**
 * One claim event and the payload its name carries. A union of pairs rather
 * than a pair of unions, so a handler that switches on the name reads the
 * matching payload: a recommendation frame's `id` names a recommendation,
 * never a claim.
 */
type SmemClaimDispatch = {
  [Event in SmemClaimEventName]: [event: Event, payload: SmemClaimDispatchPayload[Event]]
}[SmemClaimEventName]

export type SmemClaimEventHandler = (...dispatch: SmemClaimDispatch) => void

/**
 * The governance broadcasts (PRD-0242 D-10) — a fourth, additive tuple
 * sharing this module rather than extending SMEM_CLAIM_EVENTS: the
 * activation/erasure invalidators (useActivationPolicy, useErasureRuns) must
 * not see subject/episode/claim bursts, and vice versa (F-20's isolation
 * rule applied to a fourth surface). Identity-only, riding the global room
 * like every broadcast in this file.
 *
 * The execution-policy literal (PRD-0381 D-12) lands here rather than in
 * SMEM_CLAIM_EVENTS: it announces a governance setting, and every consumer of
 * this registry filters by event name, so its own hook subscribes without a
 * registry of its own and the claims invalidator never sees it.
 */
export const SMEM_GOVERNANCE_EVENTS = [
  'smem_activation_policy_changed',
  'smem_erasure_completed',
  'smem_claim_execution_policy_changed',
] as const

export type SmemGovernanceEventName = (typeof SMEM_GOVERNANCE_EVENTS)[number]

/** The payload families the governance events carry. */
export type SmemGovernanceEventPayload =
  | SmemActivationPolicyChangedBroadcast
  | SmemErasureCompletedBroadcast
  | SmemClaimExecutionPolicyBroadcast

type SmemGovernanceDispatch = [SmemGovernanceEventName, SmemGovernanceEventPayload]

/**
 * The consolidation broadcasts (PRD-0246 R-8/R-9) — a fifth, additive tuple
 * sharing this module rather than extending SMEM_GOVERNANCE_EVENTS: the
 * consolidation panel's own hooks (useConsolidationActivity,
 * useConsolidationProposals) must not run on activation/erasure bursts, and
 * the governance invalidators must not see a judged pass's proposal_created
 * burst (up to the judge budget per tick) — F-15's isolation law applied to
 * a fifth surface.
 *
 * The pause-policy literal lands here, not in SMEM_GOVERNANCE_EVENTS: F-15's
 * grouping is one registry per *consumer group*, and the toggle it announces
 * renders on the consolidation panel whose hooks already subscribe to this
 * registry — landing it in governance instead would make that one hook the
 * sole consolidation subscriber of a registry otherwise owned by the
 * activation/erasure panels, exactly the foreign-burst leak the law exists
 * to prevent. Every other registry in this file keeps its stated membership
 * untouched.
 */
export const SMEM_CONSOLIDATION_EVENTS = [
  'smem_consolidation_run_completed',
  'smem_consolidation_proposal_created',
  'smem_consolidation_proposal_applied',
  'smem_consolidation_proposal_dismissed',
  'smem_consolidation_policy_changed',
] as const

export type SmemConsolidationEventName = (typeof SMEM_CONSOLIDATION_EVENTS)[number]

/** The three payload families the five events carry. */
export type SmemConsolidationEventDispatchPayload =
  | SmemConsolidationRunCompletedBroadcast
  | SmemConsolidationProposalBroadcast
  | SmemConsolidationPolicyChangedBroadcast

type SmemConsolidationDispatch = [SmemConsolidationEventName, SmemConsolidationEventDispatchPayload]

/**
 * The ask broadcasts (PRD-0271 D-13) — a sixth, additive tuple sharing this
 * module rather than extending any registry above: the ask surface's own
 * consumer (useAskThread's exhaustedToday) must not run on subject, episode,
 * claim, governance or consolidation bursts, and those invalidators must not
 * run on a spend-guard trip.
 *
 * One literal, deliberately. Ask threads are personal rows the
 * AuditBroadcastMixin suppresses, and there is no per-user room, so a thread
 * lifecycle frame would reach every client for one operator's private
 * activity — thread state reaches the owning client through its own
 * TanStack cache instead. The spend guard is global by nature: the ceiling
 * it announces is the instance's, not one caller's.
 */
export const SMEM_ASK_EVENTS = ['smem_ask_spend_guard_tripped'] as const

export type SmemAskEventName = (typeof SMEM_ASK_EVENTS)[number]

type SmemAskDispatch = [SmemAskEventName, SmemAskSpendGuardPayload]

export class SmemLiveApi {
  private readonly events: SubscriptionRegistry<null, SmemDispatch>
  private readonly episodeEvents: SubscriptionRegistry<null, SmemEpisodeDispatch>
  private readonly claimEvents: SubscriptionRegistry<null, SmemClaimDispatch>
  private readonly governanceEvents: SubscriptionRegistry<null, SmemGovernanceDispatch>
  private readonly consolidationEvents: SubscriptionRegistry<null, SmemConsolidationDispatch>
  private readonly askEvents: SubscriptionRegistry<null, SmemAskDispatch>

  constructor(private readonly client: WebSocketClient) {
    this.events = new SubscriptionRegistry({
      // Joins the shared `global_events` room; subscribe_global is idempotent
      // server-side. There is deliberately no leave — the activity feed, the
      // profile-change and the summarisation subscriptions share the room,
      // and leaving would break those subscribers.
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
    // Same global room, a second idempotent join — not a second room. Two
    // registries rather than folding into `events` above: the two tuples have
    // disjoint payload shapes and disjoint consumers (useEpisodesRealtime,
    // useCompositionPolicy), and SMEM_EVENTS/subscribeToSmemEvents must stay
    // byte-for-byte untouched (R-11).
    this.episodeEvents = new SubscriptionRegistry({
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
    // Third disjoint registry, same room, same idempotent join — the claims
    // invalidator (useClaimsRealtime) must not run on subject or episode
    // bursts, so it gets its own registry rather than folding into either.
    this.claimEvents = new SubscriptionRegistry({
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
    // Fourth disjoint registry, same room, same idempotent join — the
    // governance invalidators (useActivationPolicy, useErasureRuns) must not
    // run on subject/episode/claim bursts, so they get their own registry.
    this.governanceEvents = new SubscriptionRegistry({
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
    // Fifth disjoint registry, same room, same idempotent join — the
    // consolidation invalidators (useConsolidationActivity,
    // useConsolidationProposals) must not run on subject/episode/claim/
    // governance bursts, so they get their own registry.
    this.consolidationEvents = new SubscriptionRegistry({
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
    // Sixth disjoint registry, same room, same idempotent join — the ask
    // surface must not re-render on a registry burst, and the five
    // invalidators above must not run on a spend-guard trip.
    this.askEvents = new SubscriptionRegistry({
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
  }

  /**
   * Subscribe to every registry broadcast through one callback, which
   * receives the event name alongside the payload. One subscription rather
   * than seven: the consumer routes cache invalidation by event name *and*
   * by payload ids, so splitting it per event would fan one shared room out
   * into seven effects that must then be kept in step.
   */
  subscribeToSmemEvents(
    callback: (event: SmemEventName, payload: SmemEventPayload) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of SMEM_EVENTS) {
      this.client.attachOnce(event, (raw) => {
        // The one transport-boundary assertion: Socket.IO hands every
        // argument through as `unknown`, and these payloads are identity-only
        // routing data — no field is ever rendered, they only choose which
        // query keys to invalidate — so the consumer's narrowing by payload
        // shape is the whole contract.
        this.events.dispatch(null, [event, raw as SmemEventPayload])
      })
    }

    return this.events.subscribe(null, ([name, payload]) =>
      callback(name, payload)
    )
  }

  /**
   * Subscribe to the episode-composition + composition-policy broadcasts
   * through one callback, mirroring subscribeToSmemEvents's shape (R-11): one
   * subscription over four events rather than four effects to keep in step.
   * Multiple independent callers may subscribe (useEpisodesRealtime and
   * useCompositionPolicy each filter to the events they own) — the
   * underlying registry fans one dispatch out to every subscriber.
   */
  subscribeToSmemEpisodeEvents(
    callback: (event: SmemEpisodeEventName, payload: SmemEpisodeEventDispatchPayload) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of SMEM_EPISODE_EVENTS) {
      this.client.attachOnce(event, (raw) => {
        this.episodeEvents.dispatch(null, [event, raw as SmemEpisodeEventDispatchPayload])
      })
    }

    return this.episodeEvents.subscribe(null, ([name, payload]) =>
      callback(name, payload)
    )
  }

  /**
   * Subscribe to the claim registry's broadcasts through one callback,
   * mirroring subscribeToSmemEvents/subscribeToSmemEpisodeEvents's shape. The
   * callback receives each event as a (name, payload) pair, so it narrows the
   * payload by switching on the name.
   */
  subscribeToSmemClaimEvents(callback: SmemClaimEventHandler): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of SMEM_CLAIM_EVENTS) {
      this.client.attachOnce(event, (raw) => {
        // The transport boundary's one assertion: Socket.IO hands the payload
        // through as unknown, and the event it arrived on is what decides its
        // shape (SmemClaimDispatchPayload).
        this.claimEvents.dispatch(null, [event, raw] as SmemClaimDispatch)
      })
    }

    return this.claimEvents.subscribe(null, (dispatch) => callback(...dispatch))
  }

  /**
   * Subscribe to the governance broadcasts through one callback, mirroring
   * subscribeToSmemClaimEvents's shape.
   */
  subscribeToSmemGovernanceEvents(
    callback: (event: SmemGovernanceEventName, payload: SmemGovernanceEventPayload) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of SMEM_GOVERNANCE_EVENTS) {
      this.client.attachOnce(event, (raw) => {
        this.governanceEvents.dispatch(null, [event, raw as SmemGovernanceEventPayload])
      })
    }

    return this.governanceEvents.subscribe(null, ([name, payload]) =>
      callback(name, payload)
    )
  }

  /**
   * Subscribe to the five consolidation broadcasts through one callback,
   * mirroring subscribeToSmemGovernanceEvents's shape exactly.
   */
  subscribeToSmemConsolidationEvents(
    callback: (
      event: SmemConsolidationEventName,
      payload: SmemConsolidationEventDispatchPayload
    ) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of SMEM_CONSOLIDATION_EVENTS) {
      this.client.attachOnce(event, (raw) => {
        this.consolidationEvents.dispatch(null, [event, raw as SmemConsolidationEventDispatchPayload])
      })
    }

    return this.consolidationEvents.subscribe(null, ([name, payload]) =>
      callback(name, payload)
    )
  }

  /**
   * Subscribe to the ask broadcasts through one callback, mirroring
   * subscribeToSmemConsolidationEvents's shape exactly.
   */
  subscribeToSmemAskEvents(
    callback: (event: SmemAskEventName, payload: SmemAskSpendGuardPayload) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of SMEM_ASK_EVENTS) {
      this.client.attachOnce(event, (raw) => {
        this.askEvents.dispatch(null, [event, raw as SmemAskSpendGuardPayload])
      })
    }

    return this.askEvents.subscribe(null, ([name, payload]) =>
      callback(name, payload)
    )
  }

  /** Re-join global_events for every live registry listener after a reconnect. */
  replay(): void {
    this.events.replay()
    this.episodeEvents.replay()
    this.claimEvents.replay()
    this.governanceEvents.replay()
    this.consolidationEvents.replay()
    this.askEvents.replay()
  }

  clear(): void {
    this.events.clear()
    this.episodeEvents.clear()
    this.claimEvents.clear()
    this.governanceEvents.clear()
    this.consolidationEvents.clear()
    this.askEvents.clear()
  }
}
